import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Queue } from '../../types';

/**
 * Props for the QueueIndicator component.
 */
interface QueueIndicatorProps {
  /** Array of queues this workflow is associated with */
  queues: Array<Pick<Queue, 'queue_id' | 'name'>>;
  /** Project ID for navigation */
  projectId: string;
  /** Size of the indicator icon */
  size?: 'sm' | 'md' | 'lg';
  /** Whether to show tooltip on hover/focus */
  showTooltip?: boolean;
}

/**
 * Inbox icon SVG component for queue indicator.
 */
const InboxIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    data-testid="inbox-icon"
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
    />
  </svg>
);

/**
 * Size class mapping for the icon.
 */
const sizeClasses: Record<'sm' | 'md' | 'lg', string> = {
  sm: 'w-4 h-4',
  md: 'w-5 h-5',
  lg: 'w-6 h-6',
};

/**
 * QueueIndicator Component
 *
 * Displays a visual indicator showing whether a workflow has queue
 * associations (reads from or writes to queues). Shows a tooltip with
 * queue names on hover and navigates to queue details on click.
 */
const QueueIndicator: React.FC<QueueIndicatorProps> = ({
  queues,
  projectId,
  size = 'md',
  showTooltip = true,
}) => {
  const navigate = useNavigate();
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);

  const handleShowTooltip = useCallback(() => {
    if (showTooltip && !isDropdownVisible) {
      setIsTooltipVisible(true);
    }
  }, [showTooltip, isDropdownVisible]);

  const handleHideTooltip = useCallback(() => {
    setIsTooltipVisible(false);
  }, []);

  const handleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (queues.length === 1) {
      // Single queue - navigate directly
      navigate(`/projects/${projectId}/queues/${queues[0].queue_id}`);
    } else if (queues.length > 1) {
      // Multiple queues - show dropdown
      setIsDropdownVisible(prev => !prev);
      setIsTooltipVisible(false);
    }
  }, [queues, projectId, navigate]);

  const handleQueueSelect = useCallback((queueId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setIsDropdownVisible(false);
    navigate(`/projects/${projectId}/queues/${queueId}`);
  }, [navigate]);

  const handleBlur = useCallback(() => {
    // Delay hiding to allow clicking on dropdown items
    setTimeout(() => {
      setIsDropdownVisible(false);
      setIsTooltipVisible(false);
    }, 150);
  }, []);

  // Return null when no queues exist
  if (!queues || queues.length === 0) {
    return null;
  }

  const iconSizeClass = sizeClasses[size];
  const colorClass = 'text-primary-600 hover:text-primary-800';

  // Build aria-label for accessibility
  const queueNames = queues.map(q => q.name).join(', ');
  const ariaLabel = queues.length === 1
    ? `Queue: ${queueNames}`
    : `${queues.length} queues: ${queueNames}`;

  return (
    <div
      data-testid="queue-indicator"
      className={`relative inline-flex items-center cursor-pointer ${colorClass}`}
      aria-label={ariaLabel}
      tabIndex={0}
      onClick={handleClick}
      onMouseEnter={handleShowTooltip}
      onMouseLeave={handleHideTooltip}
      onFocus={handleShowTooltip}
      onBlur={handleBlur}
    >
      <InboxIcon className={iconSizeClass} />

      {/* Badge for multiple queues */}
      {queues.length > 1 && (
        <span className="absolute -top-1 -right-1 bg-primary-600 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
          {queues.length}
        </span>
      )}

      {/* Tooltip */}
      {isTooltipVisible && showTooltip && !isDropdownVisible && (
        <div
          role="tooltip"
          className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 px-3 py-2 bg-gray-900 text-white text-sm rounded shadow-lg whitespace-nowrap z-50"
        >
          {/* Tooltip arrow */}
          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-b-gray-900" />

          <div className="space-y-1">
            <div className="font-medium">
              {queues.length === 1 ? 'Queue' : `${queues.length} Queues`}
            </div>
            {queues.map(q => (
              <div key={q.queue_id} className="text-gray-300">
                {q.name}
              </div>
            ))}
            <div className="text-gray-400 text-xs mt-1">
              Click to view
            </div>
          </div>
        </div>
      )}

      {/* Dropdown for multiple queues */}
      {isDropdownVisible && queues.length > 1 && (
        <div
          className="absolute top-full left-1/2 transform -translate-x-1/2 mt-1 bg-white dark:bg-cap-dark border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50 min-w-[150px]"
        >
          <div className="py-1">
            {queues.map(q => (
              <button
                key={q.queue_id}
                onClick={(e) => handleQueueSelect(q.queue_id, e)}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 focus:bg-gray-100 dark:focus:bg-gray-700"
              >
                {q.name}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default QueueIndicator;
